import { useAuth } from "react-oidc-context";
import { apiUrl } from "./auth";

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

/** Typed fetch against the API. Sends JSON, attaches the bearer token, turns errors into ApiError. */
export async function apiFetch<T>(method: string, path: string, token?: string, body?: unknown, extraHeaders: Record<string, string> = {}): Promise<T> {
  const res = await fetch(`${apiUrl}${path}`, {
    method,
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
      ...extraHeaders,
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    let message = `${res.status} ${res.statusText}`;
    try {
      const j = (await res.json()) as { message?: string | string[] };
      if (j.message) message = Array.isArray(j.message) ? j.message.join(", ") : j.message;
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(res.status, message);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/** Hook: API helpers bound to the current session's access token. */
export function useApi() {
  const auth = useAuth();
  const token = auth.user?.access_token;
  return {
    token,
    get: <T>(path: string) => apiFetch<T>("GET", path, token),
    post: <T>(path: string, body?: unknown, headers?: Record<string, string>) => apiFetch<T>("POST", path, token, body, headers),
    patch: <T>(path: string, body?: unknown) => apiFetch<T>("PATCH", path, token, body),
    put: <T>(path: string, body?: unknown) => apiFetch<T>("PUT", path, token, body),
    del: <T>(path: string) => apiFetch<T>("DELETE", path, token),
  };
}
